import type { NetworkData } from '@dynamic-labs-sdk/client';
import type {
  Chain,
  HttpTransport,
  HttpTransportConfig,
  PublicClient,
  PublicClientConfig,
} from 'viem';
import { createPublicClient, http } from 'viem';

import { mapNetworkDataToViemChain } from '../utils/mapNetworkDataToViemChain';

type CreatePublicClientFromNetworkDataProps = {
  httpTransportConfig?: HttpTransportConfig;
  networkData: NetworkData;
  publicClientConfig?: Partial<PublicClientConfig<HttpTransport, Chain>>;
};

/**
 * Creates a viem PublicClient from a given NetworkData object.
 *
 * This function configures a viem PublicClient instance for reading data from
 * an EVM-compatible blockchain using the network provider's RPC endpoint.
 *
 * @param props - The configuration object.
 * @param props.networkData - The NetworkData object containing network configuration.
 * @param props.publicClientConfig - Optional additional configuration for the viem PublicClient.
 * @param props.httpTransportConfig - Optional HTTP transport configuration.
 * @returns A configured viem PublicClient instance.
  * @not-instrumented
 */
export const createPublicClientFromNetworkData = ({
  networkData,
  publicClientConfig = {},
  httpTransportConfig = {},
}: CreatePublicClientFromNetworkDataProps): PublicClient<
  HttpTransport,
  Chain
> =>
  createPublicClient({
    chain: mapNetworkDataToViemChain(networkData),
    transport: http(networkData.rpcUrls.http[0], httpTransportConfig),
    ...publicClientConfig,
  });
